import React from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  Badge,
  Container,
  CircularProgress,
  IconButton,
  Divider,
} from "@mui/material";
import { ArrowForwardIos } from "@mui/icons-material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import Logo from "../../../../Assets/Images/Logo.png";
import { useQuery } from "@apollo/client";
import { GET_ACTIVE_POLICY_LIST } from "../../../../apis/settingsApis/policy-apis/policyApis";
import BootstrapTooltip from "../../../../components/BootstrapToolTip/BootstrapTooltip";
import { useNavigate } from "react-router-dom";
import InformationStepsCard from "../../../../components/InformationStepsCard";
import { useTheme } from "@mui/material/styles";

const EmployeePolicies = () => {
  const { loading, data } = useQuery(GET_ACTIVE_POLICY_LIST);
  const activePolicies = data?.getAllTypePolicies?.activePolicies || [];
  const theme = useTheme();

  // Separate policies based on acknowledgment status
  const acknowledgedPolicies = activePolicies.filter(
    (policy) => policy.ack === true
  );
  const pendingPolicies = activePolicies.filter((policy) => !policy.ack);

  if (loading) {
    return (
      <Container
        component="main"
        maxWidth="xs"
        sx={{ textAlign: "center", mt: 8 }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (activePolicies?.length === 0) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{ height: "75vh" }}
      >
        No Policies are available right now. Please check with your Compliance
        Officer.
      </Box>
    );
  }

  return (
    <Box sx={{ paddingTop: "20px" }}>
      <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
        <InformationStepsCard
          heading="**What should I do on this page?**"
          subtitle="This page helps you view, read, and acknowledge the policies that apply to you and your role in the organization."
          steps={[
            "Review the policies assigned to you under the **Pending policies** section",
            "Open each policy to read and understand its contents carefully.",
            "Once you've read a policy, click **Acknowledge** to confirm that you've reviewed and understood it.",
            "All assigned policies must be acknowledged within 30 days of joining the organization or once every year as part of the annual compliance review.",
            "If you have any questions or need clarification, contact your manager or the compliance team before acknowledging.",
          ]}
          footerText="💡 Acknowledging your assigned policies on time helps ensure everyone stays informed, responsible, and aligned with the organization’s security and compliance standards."
          collapsible={true}
          defaultExpanded={false}
        />
      </Box>

      {/* Pending Acknowledgment Section */}
      {pendingPolicies?.length > 0 && (
        <Box mb={4}>
          <Typography
            variant="h4"
            fontWeight="600"
            color={theme.customColors.text.primary}
            sx={{ mb: 0.5 }}
          >
            Pending Acknowledgment ({pendingPolicies?.length})
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "text.secondary", marginBottom: "20px" }}
          >
            Please review and acknowledge these policies
          </Typography>

          <Grid container spacing={2}>
            {pendingPolicies.map((policy, index) => (
              <Grid item xs={12} key={index}>
                <PolicyCard policy={policy} isPending={true} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Divider between sections */}
      {acknowledgedPolicies?.length > 0 && pendingPolicies?.length > 0 && (
        <Divider sx={{ my: 4 }} />
      )}

      {/* Acknowledged Policies Section */}
      {acknowledgedPolicies?.length > 0 && (
        <Box>
          <Typography
            variant="h4"
            fontWeight="600"
            color={theme.customColors.text.primary}
            sx={{ mb: 0.5 }}
          >
            Acknowledged Policies ({acknowledgedPolicies?.length})
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "text.secondary", marginBottom: "20px" }}
          >
            Policies you have already reviewed and acknowledged
          </Typography>

          <Grid container spacing={2}>
            {acknowledgedPolicies.map((policy, index) => (
              <Grid item xs={12} key={index}>
                <PolicyCard policy={policy} isPending={false} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

const PolicyCard = ({ policy, isPending }) => {
  const navigate = useNavigate();
  const formattedTitle = policy?.policyName?.replace(/_/g, "-").toLowerCase();

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: "12px",
        padding: "10px",
        border: "1px dashed #C5C9CF",
        "&:hover": { background: "#F7F7FC" },
        backgroundColor: isPending ? "white" : "#fafafa",
      }}
    >
      <CardContent
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            badgeContent={
              <Avatar
                sx={{
                  width: 20,
                  height: 20,
                  bgcolor: "primary.dark",
                  padding: "2px",
                }}
                src={Logo}
              />
            }
          >
            <Avatar
              sx={{
                bgcolor: "#F7F7FC",
                width: 48,
                height: 48,
                border: "1px solid #E0E0E0",
              }}
            >
              <DescriptionOutlinedIcon sx={{ color: "primary.main" }} />
            </Avatar>
          </Badge>
          <Box ml={2}>
            <Typography
              variant="h5"
              display="flex"
              alignItems="baseline"
              gap="8px"
            >
              {policy.policyDisplayName}
              <Chip
                label={isPending ? "Pending" : "Acknowledged"}
                size="small"
                sx={{
                  backgroundColor: isPending ? "#FFF4E5" : "#E8FBF3",
                  color: isPending ? "#FF9800" : "#07902E",
                  fontWeight: "500",
                }}
              />
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {policy.category === "isms document"
                ? "ISMS Document"
                : policy?.category?.charAt(0)?.toUpperCase() +
                policy?.category?.slice(1)}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton
            sx={{
              "&:hover": {
                background: "none",
              },
            }}
            onClick={() =>
              navigate(`/policy/ack/${formattedTitle}?id=${policy?.policyId}`)
            }
          >
            <ArrowForwardIos color="primary" fontSize="small" />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );
};

export default EmployeePolicies;
